import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { loadDb, setTrackReaction, Track, listPlaylists, Playlist, fetchPlaylists, deletePlaylist, getSunoGenerationRecord, updateTrack } from "../AICompose/data/schema";
import { generateOrganicPattern } from "../../utils/patternGenerator";
import { Routes } from "../../constants/routes";
import LikeIcon from "../../assets/like.svg";
import DislikeIcon from "../../assets/dislike.svg";
import ExportIcon from "../../assets/export.svg";
import BackIcon from "../../assets/back-ws.svg";
import EditIcon from "../../assets/edit-ws.svg";
import TrashIcon from "../../assets/trash.svg";
import { TrackModal } from "../AICompose/RightPanel/TrackModal";
import { TrackMenu } from "../AICompose/RightPanel/TrackMenu";
import { AddToPlaylistModal } from "../AICompose/RightPanel/Modals/AddToPlaylistModal";
import { MoveToWorkspaceModal } from "../AICompose/RightPanel/Modals/MoveToWorkspaceModal";
import { ReportBugModal } from "../AICompose/RightPanel/Modals/ReportBugModal";
import { ShareModal } from "../AICompose/RightPanel/Modals/ShareModal";
import { EditPlaylistModal } from "../AICompose/RightPanel/Modals/EditPlaylistModal";
import { FiltersModal, Filters } from "../AICompose/RightPanel/FiltersModal";
import { MusicPlayer, MusicPlayerHandle } from "../AICompose/RightPanel/MusicPlayer";
import { clearQueue } from "../AICompose/data/queue";
import { showToast } from "components/ui-kit/Toast";
import PlayIcon from "../../assets/play-player.svg";
import { CreatePersonaModal } from "components/AICompose/RightPanel/Modals/CreatePersonaModal";
import { AUDIO_LOAD_START_EVENT, AUDIO_LOAD_END_EVENT } from "../AICompose/events";
import { DeviceSize } from "constants/deviceSize";

type LibraryProps = {};

export const Library: React.FC<LibraryProps> = () => {
  const navigate = useNavigate();
  const [playingTrack, setPlayingTrack] = useState<Track | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const playerRef = useRef<MusicPlayerHandle | null>(null);
  const hasPlayer = !!playingTrack;
  const [activeTab, setActiveTab] = useState<'songs' | 'likedSongs' | 'playlists'>('songs');
  const [selectedPlaylist, setSelectedPlaylist] = useState<Playlist | null>(null);
  const [editPlaylistOpen, setEditPlaylistOpen] = useState(false);
  const [editingPlaylist, setEditingPlaylist] = useState<Playlist | null>(null);
  const [editSelectedPlaylistOpen, setEditSelectedPlaylistOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedTrack, setSelectedTrack] = useState<Track | null>(null);
  const [selectedTracks, setSelectedTracks] = useState<Set<string>>(new Set());
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [submenuOpen, setSubmenuOpen] = useState<'share' | 'download' | 'report' | null>(null);
  const [addToPlaylistTrack, setAddToPlaylistTrack] = useState<Track | null>(null);
  const [moveToWorkspaceTrack, setMoveToWorkspaceTrack] = useState<Track | null>(null);
  const [reportBugTrack, setReportBugTrack] = useState<Track | null>(null);
  const [shareTrack, setShareTrack] = useState<Track | null>(null);
  const [sharePlaylist, setSharePlaylist] = useState<Playlist | null>(null);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    liked: false,
    fullSongs: false,
    hasPersonas: false,
    hideDisliked: false,
  });
  const [sortBy, setSortBy] = useState<'newest' | 'oldest'>('newest');
  const [createPersonaTrack, setCreatePersonaTrack] = useState<Track | null>(null);
  const [deletingPlaylistId, setDeletingPlaylistId] = useState<string | null>(null);
  const [audioLoadingTrackIds, setAudioLoadingTrackIds] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set<string>();
    const anyWindow = window as any;
    const globalSet: Set<string> | undefined = anyWindow.__audioLoadingTrackIds;
    return globalSet ? new Set(globalSet) : new Set<string>();
  });
  
  // Polling state for tracks that are still generating
  const [pendingTasks, setPendingTasks] = useState<Array<{ trackId: string; taskId: string; slotIndex: number; attempts: number }>>([]);
  
  const POLLING_INTERVAL_MS = 10000;
  const MAX_POLL_ATTEMPTS = 24;

  const formatDuration = (durationSec?: number) => {
    if (!durationSec || durationSec <= 0) return null;
    const total = Math.round(durationSec);
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Listen for track creation events
  useEffect(() => {
    const handleTrackCreated = () => {
      setRefreshKey(k => k + 1);
    };
    window.addEventListener('trackCreated', handleTrackCreated);
    return () => window.removeEventListener('trackCreated', handleTrackCreated);
  }, []);
  
  // Listen for track reaction changes
  useEffect(() => {
    const handleReactionChanged = () => {
      setRefreshKey(k => k + 1);
    };
    window.addEventListener('trackReactionChanged', handleReactionChanged);
    return () => window.removeEventListener('trackReactionChanged', handleReactionChanged);
  }, []);

  // Live-update track metadata (title, description, styles, artwork, audioUrl, etc)
  useEffect(() => {
    const handleTrackUpdated = () => {
      setRefreshKey(k => k + 1);
    };
    window.addEventListener('trackUpdated', handleTrackUpdated);
    return () => window.removeEventListener('trackUpdated', handleTrackUpdated);
  }, []);

  useEffect(() => {
    const handleAudioLoadStart = (event: Event) => {
      const detail = (event as CustomEvent<{ trackId: string }>).detail;
      if (!detail?.trackId) return;
      setAudioLoadingTrackIds(prev => {
        const next = new Set(prev);
        next.add(detail.trackId);
        return next;
      });
    };
    const handleAudioLoadEnd = (event: Event) => {
      const detail = (event as CustomEvent<{ trackId: string }>).detail;
      if (!detail?.trackId) return;
      setAudioLoadingTrackIds(prev => {
        if (!prev.has(detail.trackId)) return prev;
        const next = new Set(prev);
        next.delete(detail.trackId);
        return next;
      });
    };
    window.addEventListener(AUDIO_LOAD_START_EVENT, handleAudioLoadStart as EventListener);
    window.addEventListener(AUDIO_LOAD_END_EVENT, handleAudioLoadEnd as EventListener);
    return () => {
      window.removeEventListener(AUDIO_LOAD_START_EVENT, handleAudioLoadStart as EventListener);
      window.removeEventListener(AUDIO_LOAD_END_EVENT, handleAudioLoadEnd as EventListener);
    };
  }, []);

  const isTrackLoading = useCallback(
    (track: Track) => Boolean(track?.params?.isLoading) || audioLoadingTrackIds.has(track.id),
    [audioLoadingTrackIds]
  );
  
  useEffect(() => {
    let isMounted = true;
    const syncPlaylists = async () => {
      try {
        await fetchPlaylists();
        if (isMounted) {
          setRefreshKey(k => k + 1);
        }
      } catch (error) {
        console.error("Unable to fetch playlists", error);
      }
    };
    syncPlaylists();
    return () => {
      isMounted = false;
    };
  }, []);
  
  // Initialize pending tasks from tracks that are still loading
  useEffect(() => {
    const db = loadDb();
    const tracks = Object.values(db.tracks).filter(t => !t.trashed);
    const pending: Array<{ trackId: string; taskId: string; slotIndex: number; attempts: number }> = [];
    
    for (const track of tracks) {
      const params = track.params || {};
      const isLoading = params.isLoading === true;
      const hasTaskId = params.sunoJob?.taskId || params.taskId;
      const hasAudioUrl = params.audioUrl || params.localAudioUrl;
      
      // If track is loading or has taskId but no audioUrl, add to pending
      if (hasTaskId && (isLoading || !hasAudioUrl)) {
        const taskId = params.sunoJob?.taskId || params.taskId;
        const slotIndex = typeof params.slotIndex === 'number' ? params.slotIndex : 0;
        
        if (taskId && typeof taskId === 'string') {
          pending.push({
            trackId: track.id,
            taskId,
            slotIndex,
            attempts: 0,
          });
        }
      }
    }
    
    if (pending.length > 0) {
      setPendingTasks(pending);
    }
  }, []);
  
  // Polling mechanism for tracks that are still generating
  useEffect(() => {
    if (!pendingTasks.length) return;

    const interval = setInterval(async () => {
      const currentTasks = [...pendingTasks];
      const processedTaskIds = new Set<string>();

          for (const task of currentTasks) {
        // Process each unique taskId only once
        if (processedTaskIds.has(task.taskId)) continue;
        processedTaskIds.add(task.taskId);

        try {
          const record = await getSunoGenerationRecord(task.taskId);
          
          const recordData = record?.data;
          if (!recordData?.job) {
            console.warn(`[Library] No job data for task ${task.taskId}`);
            continue;
          }

          // Get tracks array from data.tracks - create a deep copy to avoid mutations
          const tracksValue = recordData.tracks;
          let tracksArray: any[] = [];
          if (Array.isArray(tracksValue)) {
            tracksArray = tracksValue.map(track => {
              if (!track) return track;
              return JSON.parse(JSON.stringify(track));
            });
          } else if (tracksValue && typeof tracksValue === 'object') {
            tracksArray = Object.values(tracksValue).map(track => {
              if (!track) return track;
              return JSON.parse(JSON.stringify(track));
            });
          }

          // Get all job tasks for this taskId
          const jobTasks = currentTasks.filter(item => item.taskId === task.taskId);

          // Check job status
          const rawStatus = (recordData.job.status || '').toLowerCase();
          const successStatuses = new Set(['completed', 'succeeded', 'success', 'ready']);
          const failedStatuses = new Set(['failed', 'error', 'cancelled']);
          const isFailed = failedStatuses.has(rawStatus);
          const normalizedStatus = isFailed ? rawStatus : (tracksArray.length > 0 ? 'completed' : rawStatus);

          // Check if all tracks have data and audio URLs
          let allTracksHaveData = true;
          let allTracksHaveAudioUrl = true;

          for (const jobTask of jobTasks) {
            const slotIndex = jobTask.slotIndex;
            
            if (slotIndex < 0 || slotIndex >= tracksArray.length) {
              console.warn(`[Library] Invalid slotIndex ${slotIndex} for track ${jobTask.trackId}, tracksArray length: ${tracksArray.length}`);
              allTracksHaveData = false;
              continue;
            }

            const trackData = tracksArray[slotIndex] || null;
            
            if (!trackData) {
              allTracksHaveData = false;
              continue;
            }

            const trackUuid = trackData.uuid || trackData.suno_audio_id || null;
            let localAudioUrl = trackData.local_audio_url;
            
            if (localAudioUrl && trackUuid && !localAudioUrl.includes(trackUuid)) {
              console.warn(`[Library] Audio URL mismatch for track ${jobTask.trackId} (slot ${slotIndex})`);
            }

            if (!localAudioUrl || typeof localAudioUrl !== 'string' || !localAudioUrl.trim()) {
              allTracksHaveAudioUrl = false;
            }

            const localImageUrl = 
              trackData.local_image_url ||
              trackData.localImageUrl ||
              trackData?.media?.local_image_url ||
              trackData?.media?.localImageUrl ||
              null;
            const remoteImageUrl = trackData.image_url || trackData.imageUrl || null;
            const resolvedImageUrl = localImageUrl || remoteImageUrl || null;

            // Update track with data from server
            updateTrack(jobTask.trackId, (track) => {
              track.params = {
                ...track.params,
                sunoJob: {
                  ...(track.params?.sunoJob || {}),
                  status: normalizedStatus,
                },
                sunoResult: record,
                tracks: tracksArray,
                slotIndex: slotIndex,
                isLoading: false,
                error: isFailed ? (recordData.job?.error_message || 'Generation failed') : undefined,
              };

              // Persist UUID for sharing and future lookups
              if (trackUuid) {
                track.params.uuid = trackUuid;
                // Backwards-compatible alias used in some code paths
                (track.params as any).trackUuid = (track.params as any).trackUuid ?? trackUuid;
              }

              if (localAudioUrl) {
                track.params.localAudioUrl = localAudioUrl;
                track.params.audioUrl = localAudioUrl;
              } else {
                delete track.params.localAudioUrl;
                delete track.params.audioUrl;
              }

              if (localImageUrl) {
                track.params.localImageUrl = localImageUrl;
              } else {
                delete track.params.localImageUrl;
              }
              if (resolvedImageUrl) {
                track.params.imageUrl = resolvedImageUrl;
              } else {
                delete track.params.imageUrl;
              }

              if (trackData.suno_audio_id) {
                track.params.sunoAudioId = trackData.suno_audio_id;
              }
              if (trackData.audio_id || trackData.audioId) {
                track.params.audioId = trackData.audio_id || trackData.audioId;
              }
              if (trackData.id) {
                track.params.sunoTrackId = trackData.id;
              }
              if (trackData.model_name) {
                track.params.modelName = trackData.model_name;
              }
              if (trackData.tags) {
                track.params.tags = trackData.tags;
              }

              if (typeof trackData.duration === 'number') {
                track.durationSec = trackData.duration;
              }
              if (trackData.prompt && !track.description) {
                track.description = trackData.prompt;
              }
              if (trackData.title) {
                const requestedTitle = (track.params?.sunoRequest?.title ?? '').toString().trim();
                const userProvidedTitle = Boolean(requestedTitle) && !/^song$/i.test(requestedTitle);
                const placeholderTitle = (track.title || '').trim();
                const isGenericTitle = !placeholderTitle || /^(song|untitled track)/i.test(placeholderTitle);

                if (!userProvidedTitle || isGenericTitle) {
                  track.title = trackData.title;
                }
              }
              if (!track.description && !isFailed) {
                track.description = 'Generated track';
              }
            });
          }

          const shouldStopPolling = isFailed || (allTracksHaveData && allTracksHaveAudioUrl);

          if (shouldStopPolling) {
            setPendingTasks(prev => prev.filter(item => item.taskId !== task.taskId));
            setRefreshKey(k => k + 1);
            window.dispatchEvent(new CustomEvent('trackCreated'));
          }

        } catch (error) {
          console.warn(`[Library] Failed to poll task ${task.taskId}:`, error);
          
          setPendingTasks(prev => {
            const updated: Array<{ trackId: string; taskId: string; slotIndex: number; attempts: number }> = [];
            prev.forEach(item => {
              if (item.taskId !== task.taskId) {
                updated.push(item);
                return;
              }
              const attempts = (item.attempts || 0) + 1;
              if (attempts >= MAX_POLL_ATTEMPTS) {
                updateTrack(item.trackId, (track) => {
                  track.params = {
                    ...track.params,
                    isLoading: false,
                    error: 'Unable to fetch generation result. Please try again later.',
                  };
                });
                setRefreshKey(k => k + 1);
                window.dispatchEvent(new CustomEvent('trackCreated'));
              } else {
                updated.push({ ...item, attempts });
              }
            });
            return updated;
          });
        }
      }
    }, POLLING_INTERVAL_MS);

    return () => {
      clearInterval(interval);
    };
  }, [pendingTasks]);
  
  // Close menu when clicking outside
  useEffect(() => {
    if (!menuOpenId) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-track-menu]')) {
        setMenuOpenId(null);
        setSubmenuOpen(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [menuOpenId]);

  const handlePlayerToggle = () => {
    if (playerRef.current) {
      playerRef.current.togglePlay();
    }
  };

  const [sourceTracksForPlayer, setSourceTracksForPlayer] = useState<Track[]>([]);

  const handlePlayTrack = (track: Track | null) => {
    if (!track) {
      setPlayingTrack(null);
      setSourceTracksForPlayer([]);
      return;
    }
    
    // Clear queue when starting a new track (not from Add to Queue)
    clearQueue();
    
    // Determine source tracks based on context
    let sourceTracks: Track[] = [];
    
    if (selectedPlaylist) {
      // Playing from a playlist - use playlist tracks
      sourceTracks = playlistTracks;
    } else if (activeTab === 'likedSongs') {
      // Playing from liked songs - use only liked tracks
      const db = loadDb();
      sourceTracks = Object.values(db.tracks)
        .filter(t => !t.trashed && t.reaction === 'like')
        .sort((a, b) => b.createdAt - a.createdAt);
    } else if (activeTab === 'songs') {
      // Playing from all songs - use filtered tracks
      // Note: filteredTracks already includes all tracks if no filters are applied
      sourceTracks = filteredTracks;
    }
    
    setSourceTracksForPlayer(sourceTracks);
    setPlayingTrack(track);
  };

  // Get all tracks from all workspaces
  const allTracks = useMemo(() => {
    const db = loadDb();
    const tracks: Track[] = [];
    
    // Get all tracks from all non-trashed workspaces
    Object.values(db.workspaces)
      .filter(ws => !ws.trashed)
      .forEach(ws => {
        ws.trackIds.forEach(trackId => {
          const track = db.tracks[trackId];
          if (track && !track.trashed) {
            tracks.push(track);
          }
        });
      });
    
    // Sort by createdAt (newest first)
    return tracks.sort((a, b) => b.createdAt - a.createdAt);
  }, [refreshKey]);

  // Get all playlists
  const playlists = useMemo(() => {
    return listPlaylists();
  }, [refreshKey]);

  // Get tracks for selected playlist
  const playlistTracks = useMemo(() => {
    if (!selectedPlaylist) return [];
    const db = loadDb();
    return selectedPlaylist.trackIds
      .map(id => db.tracks[id])
      .filter(Boolean)
      .filter(t => !t.trashed)
      .sort((a, b) => b.createdAt - a.createdAt);
  }, [selectedPlaylist, refreshKey]);

  const handlePlaylistClick = (playlist: Playlist) => {
 
    setSelectedPlaylist(playlist);
  };

  const handleDeletePlaylist = async (playlist: Playlist) => {
    if (playlist.id === 'playlist_liked_songs') {
      showToast("Cannot delete default playlist", undefined, "error");
      return;
    }
    const confirmed = window.confirm(`Delete playlist "${playlist.name}"?`);
    if (!confirmed) return;

    setDeletingPlaylistId(playlist.id);
    try {
      await deletePlaylist(playlist.id);
      await fetchPlaylists();
      setRefreshKey(k => k + 1);
      if (selectedPlaylist?.id === playlist.id) {
        setSelectedPlaylist(null);
      }
      showToast("Playlist deleted", undefined, "success");
    } catch (error) {
      console.error("Failed to delete playlist", error);
      showToast("Unable to delete playlist. Please try again.", undefined, "error");
    } finally {
      setDeletingPlaylistId(null);
    }
  };

  const handlePlayPlaylist = () => {
    if (!selectedPlaylist || playlistTracks.length === 0) return;
    clearQueue();
    setSourceTracksForPlayer(playlistTracks);
    const firstTrack = playlistTracks[0];
    setPlayingTrack(firstTrack);
  };

  const handleBackToLibrary = () => {
    setSelectedPlaylist(null);
  };

  const handleCreateSong = () => {
    navigate(Routes.AICompose);
  };

  const handleCreatePlaylist = () => {
    setEditingPlaylist(null);
    setEditPlaylistOpen(true);
  };

  // Filter tracks based on active tab and filters
  const filteredTracks = useMemo(() => {
    // If playlists tab, return empty tracks (we'll show playlists instead)
    if (activeTab === 'playlists') {
      return [];
    }
    
    let tracks = allTracks;
    
    // Filter by tab
    if (activeTab === 'likedSongs') {
      tracks = tracks.filter(t => t.reaction === 'like');
    } else if (activeTab === 'songs') {
      // All songs
    }
    
    // Apply filters
    if (filters.liked) {
      tracks = tracks.filter((t: any) => t.reaction === 'like');
    }
    if (filters.hideDisliked) {
      tracks = tracks.filter((t: any) => t.reaction !== 'dislike');
    }
    if (filters.fullSongs) {
      tracks = tracks.filter((t: any) => t.fullSongs !== false); // Show only if fullSongs is true or undefined
    }
    if (filters.hasPersonas) {
      tracks = tracks.filter((t: any) => t.hasPersonas === true);
    }
    
    // Search filter
    if (query.trim()) {
      const searchLower = query.toLowerCase();
      tracks = tracks.filter((t: any) => 
        t.title?.toLowerCase().includes(searchLower) ||
        t.params?.songDescription?.toLowerCase().includes(searchLower) ||
        t.params?.stylesDescription?.toLowerCase().includes(searchLower)
      );
    }
    
    // Sort
    if (sortBy === 'oldest') {
      tracks = [...tracks].sort((a, b) => a.createdAt - b.createdAt);
    } else if (sortBy === 'newest') {
      tracks = [...tracks].sort((a, b) => b.createdAt - a.createdAt);
    }
    
    return tracks;
  }, [allTracks, activeTab, filters, query, sortBy]);

  return (
    <Container $hasPlayer={hasPlayer}>
      {!selectedPlaylist && (
        <Header>
          <Tabs>
            <Tab $active={activeTab === 'songs'} onClick={() => setActiveTab('songs')}>
              Songs
            </Tab>
            <Tab $active={activeTab === 'likedSongs'} onClick={() => setActiveTab('likedSongs')}>
              Liked Songs
            </Tab>
            <Tab $active={activeTab === 'playlists'} onClick={() => setActiveTab('playlists')}>
              Playlists
            </Tab>
          </Tabs>
        </Header>
      )}
      {!selectedPlaylist && activeTab !== 'playlists' && (
        <FixedControls $hasModal={!!selectedTrack}>
          <ControlsRow>
            <SearchInput
              name="search"
              placeholder="Search"
              value={query}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
            />
            <DropdownWrapper>
              <PillWithCaret
                $active={isFiltersOpen}
                onClick={() => {
                  setIsFiltersOpen(v => !v);
                  setIsSortOpen(false);
                }}
              >
                {(() => {
                  const activeCount =
                    (filters.liked ? 1 : 0) +
                    (filters.fullSongs ? 1 : 0) +
                    (filters.hasPersonas ? 1 : 0) +
                    (filters.hideDisliked ? 1 : 0);
                  return activeCount > 0 ? `Filters (${activeCount})` : "Filters";
                })()}
              </PillWithCaret>
              {isFiltersOpen && (
                <FiltersModal
                  filters={filters}
                  onChange={setFilters}
                  onClose={() => setIsFiltersOpen(false)}
                />
              )}
            </DropdownWrapper>
            <DropdownWrapper>
              <PillWithCaret $active={isSortOpen} onClick={() => { setIsSortOpen(v => !v); setIsFiltersOpen(false); }}>
                {sortBy === 'newest' ? 'Newest' : 'Oldest'}
              </PillWithCaret>
              {isSortOpen && (
                <DropdownMenu onClick={(e) => e.stopPropagation()}>
                  <DropdownItem>
                    <label>
                      <span>Newest</span>
                      <input type="checkbox" checked={sortBy === 'newest'} onChange={() => { setSortBy('newest'); setIsSortOpen(false); }} />
                    </label>
                  </DropdownItem>
                  <DropdownItem>
                    <label>
                      <span>Oldest</span>
                      <input type="checkbox" checked={sortBy === 'oldest'} onChange={() => { setSortBy('oldest'); setIsSortOpen(false); }} />
                    </label>
                  </DropdownItem>
                </DropdownMenu>
              )}
            </DropdownWrapper>
          </ControlsRow>
        </FixedControls>
      )}
      <ContentWrapper $hasModal={!!selectedTrack} $hasPlayer={hasPlayer}>
        {selectedPlaylist ? (
          <PlaylistViewContainer>
            <PlaylistHeaderRow>
              <BackButton onClick={handleBackToLibrary}>
                <BackIcon />
              </BackButton>
              <PlaylistHeader>
                <PlaylistCover>
                  {selectedPlaylist.id === 'playlist_liked_songs' ? (
                    <PlaylistCoverImage>
                      <LikeIconWrapper>
                        <LikeIcon color="#fff" width="20px" height="20px" />
                      </LikeIconWrapper>
                    </PlaylistCoverImage>
                  ) : selectedPlaylist.coverImage ? (
                    (console.log('[Library] playlist cover render', {
                      playlistId: selectedPlaylist.id,
                      coverImage: selectedPlaylist.coverImage,
                    }),
                    <PlaylistCoverImageImg src={selectedPlaylist.coverImage} alt={selectedPlaylist.name} />)
                  ) : (
                    <PlaylistCoverImage $gradient={getGradientForPlaylist(selectedPlaylist.id)} />
                  )}
                </PlaylistCover>
                <PlaylistHeaderInfo>
                  <PlaylistTitleRow>
                    <PlaylistViewTitle>{selectedPlaylist.name}</PlaylistViewTitle>
                    {selectedPlaylist.id !== 'playlist_liked_songs' && (
                      <PlaylistActionsWrapper>
                        <EditPlaylistButton onClick={() => setEditSelectedPlaylistOpen(true)}>
                          <EditIcon />
                        </EditPlaylistButton>
                        <DeletePlaylistButton
                          onClick={() => handleDeletePlaylist(selectedPlaylist)}
                          disabled={deletingPlaylistId === selectedPlaylist.id}
                        >
                          <TrashIcon />
                        </DeletePlaylistButton>
                      </PlaylistActionsWrapper>
                    )}
                  </PlaylistTitleRow>
                  <PlaylistHeaderActions>
                    <PlayButton onClick={handlePlayPlaylist} disabled={playlistTracks.length === 0}>
                      <PlayButtonText>Play playlist</PlayButtonText>
                      <PlayIcon />
                    </PlayButton>
                    <ShareButton onClick={() => setSharePlaylist(selectedPlaylist)}>
                      <ExportIcon />
                    </ShareButton>
                  </PlaylistHeaderActions>
                </PlaylistHeaderInfo>
              </PlaylistHeader>
            </PlaylistHeaderRow>
            {playlistTracks.length === 0 ? (
              <EmptyPlaylistState>
                <EmptyPlaylistText>
                  Start your playlist by creating a song or adding an existing song from your Library
                </EmptyPlaylistText>
                <EmptyPlaylistActions>
                  <CreateSongButton onClick={handleCreateSong}>
                    Create Song
                  </CreateSongButton>
                  <GoToLibraryButton onClick={handleBackToLibrary}>
                    Go to Library
                  </GoToLibraryButton>
                </EmptyPlaylistActions>
              </EmptyPlaylistState>
            ) : (
              <PlaylistTrackList>
                {playlistTracks.map((track: Track) => {
                  // Subtitle based on mode and latest server data
                  const params = (track.params || {}) as Record<string, any>;
                  const isCustomMode =
                    params.customMode === true ||
                    params.mode === 'custom';
                  const mode = isCustomMode ? 'custom' : 'easy';

                  let subtitleText = '';
                  if (mode === 'easy') {
                    const prompt: string = (
                      params.songDescription ??
                      params.prompt ??
                      track.description ??
                      ''
                    ).toString();
                    subtitleText = prompt;
                  } else {
                    const styles: string = (
                      params.stylesDescription ??
                      params.style ??
                      ''
                    ).toString();
                    subtitleText = styles || (track.description || '').toString();
                  }

                  // Replace newlines with spaces, trim and clamp
                  const cleanedSubtitle = subtitleText
                    ? subtitleText.replace(/\s+/g, ' ').trim()
                    : '';
                  const isMobile =
                    typeof window !== 'undefined'
                      ? window.innerWidth <= 768
                      : false;
                  const baseMax = selectedTrack ? 100 : 160;
                  const maxLength = isMobile ? Math.max(30, Math.floor(baseMax / 4)) : baseMax;
                  const subtitleShort = cleanedSubtitle
                    ? cleanedSubtitle.length > maxLength
                      ? `${cleanedSubtitle.slice(0, maxLength)}...`
                      : cleanedSubtitle
                    : '';
                  const fullTitle = (track.title || 'Song').toString();
                  const titleMax = isMobile ? 17 : 30;
                  const displayTitle =
                    fullTitle.length > titleMax ? `${fullTitle.slice(0, 10)}...` : fullTitle;
                  const reaction = track.reaction || null;
                  const loading = isTrackLoading(track);
                  const playableUrl = track.params?.localAudioUrl || null;
                  const artworkUrl =
                    track.params?.localImageUrl ||
                    track.params?.imageUrl ||
                    null;
                  const durationLabel = formatDuration(track.durationSec);
                  
                  return (
                    <TrackItem key={track.id}>
                      <CheckboxWrapper>
                        <Checkbox 
                          $visible={selectedTracks.has(track.id)}
                          type="checkbox"
                          checked={selectedTracks.has(track.id)}
                          onChange={(e) => {
                            const newSelected = new Set(selectedTracks);
                            if (e.target.checked) {
                              newSelected.add(track.id);
                            } else {
                              newSelected.delete(track.id);
                            }
                            setSelectedTracks(newSelected);
                          }}
                        />
                      </CheckboxWrapper>
                      <ThumbnailWrapper
                        $isLoading={loading}
                        onClick={() => {
                          if (loading) return;
                          if (!playableUrl) {
                            showToast("Audio is not available yet. Please wait a bit longer.");
                            console.warn('[Library] Missing playable URL for track', {
                              trackId: track.id,
                              params: track.params,
                            });
                            return;
                          }
                          const isThisTrack = playingTrack?.id === track.id;
                          if (isThisTrack) {
                            handlePlayerToggle();
                          } else {
                            handlePlayTrack(track);
                          }
                        }}
                      >
                        <Thumbnail $imageUrl={artworkUrl} />
                        <PlayOverlay $isLoading={loading}>
                          {loading ? (
                            <LoaderSpinner />
                          ) : playingTrack?.id === track.id && isPlaying ? (
                            <svg viewBox="0 0 24 24" style={{ width: '20px', height: '20px', fill: '#6777FF'}}>
                              <rect x="6" y="4" width="4" height="16" fill='#6777FF' />
                              <rect x="14" y="4" width="4" height="16" fill='#6777FF' />
                            </svg>
                          ) : (
                            <PlayIcon />
                          )}
                        </PlayOverlay>
                        {durationLabel && !loading && (
                          <DurationBadge>{durationLabel}</DurationBadge>
                        )}
                      </ThumbnailWrapper>
                      <TrackInfo onClick={() => {
                        if (loading) return;
                        setSelectedTrack(track);
                      }}>
                        <TrackTitle>
                          {displayTitle}
                        </TrackTitle>
                        <TrackSubtitle>{subtitleShort}</TrackSubtitle>
                        <TrackActions onClick={(e) => e.stopPropagation()}>
                          <ReactionButton
                            title="Like"
                            $active={reaction === 'like'}
                            disabled={loading}
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (loading) return;
                              const next = reaction === 'like' ? null : 'like'; 
                              try {
                                await setTrackReaction(track.id, next as any);
                              } catch (error) {
                                console.error('Failed to set track reaction:', error);
                                showToast('Failed to update reaction', undefined, 'error');
                              }
                            }}
                          >
                            <LikeIcon />
                          </ReactionButton>
                          <ReactionButton
                            title="Dislike"
                            $active={reaction === 'dislike'}
                            disabled={loading}
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (loading) return;
                              const next = reaction === 'dislike' ? null : 'dislike'; 
                              try {
                                await setTrackReaction(track.id, next as any);
                              } catch (error) {
                                console.error('Failed to set track reaction:', error);
                                showToast('Failed to update reaction', undefined, 'error');
                              }
                            }}
                          >
                            <DislikeIcon />
                          </ReactionButton>
                          <ActionButton 
                            title="Export"
                            disabled={loading}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (loading) return;
                            }}
                          >
                            <ExportIcon />
                          </ActionButton>
                        </TrackActions>
                      </TrackInfo>
                      <MoreWrapper onClick={(e) => e.stopPropagation()} data-track-menu>
                        {!loading && (
                          <MoreButton onClick={(e) => { e.stopPropagation(); setMenuOpenId(menuOpenId === track.id ? null : track.id); setSubmenuOpen(null); }}>⋯</MoreButton>
                        )}
                        {!loading && menuOpenId === track.id && (
                          <TrackMenu
                            trackId={track.id}
                            track={track}
                            onClose={() => { setMenuOpenId(null); setSubmenuOpen(null); }}
                            onAddToPlaylist={() => { setAddToPlaylistTrack(track); setMenuOpenId(null); setSubmenuOpen(null); }}
                            onMoveToWorkspace={() => { setMoveToWorkspaceTrack(track); setMenuOpenId(null); setSubmenuOpen(null); }}
                            onCreatePersona={() => { setCreatePersonaTrack(track); setMenuOpenId(null); setSubmenuOpen(null); }}
                            onAddToQueue={() => {}}
                            onMoveToTrash={() => { setRefreshKey(k => k + 1); setSelectedTracks(new Set()); }}
                            onReportBug={() => { 
                              setReportBugTrack(track); 
                            }}
                            onReportInappropriate={() => { 
                              showToast("Marked, thanks!", undefined, 'success');
                            }}
                            onShareTo={() => { 
                              setShareTrack(track); 
                            }}
                            selectedTracks={selectedTracks}
                            submenuOpen={submenuOpen}
                        onSubmenuOpen={setSubmenuOpen}
                          />
                        )}
                      </MoreWrapper>
                    </TrackItem>
                  );
                })}
              </PlaylistTrackList>
            )}
          </PlaylistViewContainer>
        ) : activeTab === 'playlists' ? (
          <PlaylistList>
            <CreatePlaylistItem onClick={handleCreatePlaylist}>
              <CreatePlaylistIcon>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              </CreatePlaylistIcon>
            </CreatePlaylistItem>
            {playlists.map((playlist: Playlist) => {
              const isLikedSongs = playlist.id === 'playlist_liked_songs';
              const hasGradientCover = playlist.coverImage?.startsWith('gradient:') ?? false;

              const getGradientFromKey = (key: string): string => {
                const gradients = [
                  'linear-gradient(135deg, #ff9a9e, #fecfef)',
                  'linear-gradient(135deg, #a1c4fd, #c2e9fb)',
                  'linear-gradient(135deg, #ffecd2, #fcb69f)',
                  'linear-gradient(135deg, #ff9a56, #ffad56)',
                  'linear-gradient(135deg, #667eea, #764ba2)',
                  'linear-gradient(135deg, #f093fb, #f5576c)',
                  'linear-gradient(135deg, #4facfe, #00f2fe)',
                  'linear-gradient(135deg, #43e97b, #38f9d7)',
                ];
                const indexStr = key.split(':')[1] ?? '0';
                const index = parseInt(indexStr, 10);
                if (Number.isNaN(index)) return gradients[0];
                return gradients[((index % gradients.length) + gradients.length) % gradients.length];
              };

              const gradient =
                hasGradientCover
                  ? getGradientFromKey(playlist.coverImage as string)
                  : getGradientForPlaylist(playlist.id);

              return (
                <PlaylistItem key={playlist.id}>
                  <PlaylistItemHeader>
                    <PlaylistItemMain onClick={() => handlePlaylistClick(playlist)}>
                      <PlaylistThumbnail>
                        {isLikedSongs ? (
                          <PlaylistThumbnailImage>
                            <LikeIconWrapper>
                              <LikeIcon color="#fff" width="20px" height="20px" />
                            </LikeIconWrapper>
                          </PlaylistThumbnailImage>
                        ) : hasGradientCover ? (
                          <PlaylistThumbnailImage $gradient={gradient} />
                        ) : playlist.coverImage ? (
                          (console.debug('[Library] playlist thumbnail render', {
                            playlistId: playlist.id,
                            coverImage: playlist.coverImage,
                          }),
                          <PlaylistThumbnailImageImg src={playlist.coverImage} alt={playlist.name} />)
                        ) : (
                          <PlaylistThumbnailImage $gradient={gradient} />
                        )}
                      </PlaylistThumbnail>
                      <PlaylistInfo>
                        <PlaylistName>{playlist.name}</PlaylistName>
                        <PlaylistCount>{playlist.trackIds.length} {playlist.trackIds.length === 1 ? 'song' : 'songs'}</PlaylistCount>
                      </PlaylistInfo>
                    </PlaylistItemMain>
                  </PlaylistItemHeader>
                </PlaylistItem>
              );
            })}
          </PlaylistList>
        ) : filteredTracks.length === 0 ? (
          <EmptyState>
            <EmptyText>
              {filters.liked ||
              filters.fullSongs ||
              filters.hasPersonas ||
              filters.hideDisliked
                ? "No songs found matching your filters"
                : "No songs found."}
            </EmptyText>
          </EmptyState>
        ) : (
          <TrackList>
                {filteredTracks.map((track: any) => {
              const params = (track.params || {}) as Record<string, any>;
              const isCustomMode =
                params.customMode === true ||
                params.mode === 'custom';
              const mode = isCustomMode ? 'custom' : 'easy';

              let subtitleText = '';
              if (mode === 'easy') {
                const prompt: string = (
                  params.songDescription ??
                  params.prompt ??
                  track.description ??
                  ''
                ).toString();
                subtitleText = prompt;
              } else {
                const styles: string = (
                  params.stylesDescription ??
                  params.style ??
                  ''
                ).toString();
                subtitleText = styles || (track.description || '').toString();
              }

              const cleanedSubtitle = subtitleText
                ? subtitleText.replace(/\s+/g, ' ').trim()
                : '';
              const isMobile =
                typeof window !== 'undefined'
                  ? window.innerWidth <= 768
                  : false;
              const baseMax = selectedTrack ? 100 : 170;
              const maxLength = isMobile ? Math.max(30, Math.floor(baseMax / 4)) : baseMax;
              const subtitleShort = cleanedSubtitle
                ? cleanedSubtitle.length > maxLength
                  ? `${cleanedSubtitle.slice(0, maxLength)}...`
                  : cleanedSubtitle
                : '';
              const fullTitle = (track.title || 'Song').toString();
              const titleMax = isMobile ? 17 : 30;
              const displayTitle =
                fullTitle.length > titleMax ? `${fullTitle.slice(0, 10)}...` : fullTitle;
              const reaction = track.reaction || null;
              const loading = isTrackLoading(track);
              const playableUrl = track.params?.localAudioUrl || null;
              const artworkUrl =
                track.params?.localImageUrl ||
                track.params?.imageUrl ||
                null;
              const durationLabel = formatDuration(track.durationSec);
              
              return (
                <TrackItem key={track.id}>
                  <CheckboxWrapper>
                    <Checkbox 
                      $visible={selectedTracks.has(track.id)}
                      type="checkbox"
                      checked={selectedTracks.has(track.id)}
                      onChange={(e) => {
                        const newSelected = new Set(selectedTracks);
                        if (e.target.checked) {
                          newSelected.add(track.id);
                        } else {
                          newSelected.delete(track.id);
                        }
                        setSelectedTracks(newSelected);
                      }}
                    />
                  </CheckboxWrapper>
                  <ThumbnailWrapper
                    $isLoading={loading}
                    onClick={() => {
                      if (loading) return;
                      if (!playableUrl) {
                        showToast("Audio is not available yet. Please wait a bit longer.");
                        console.warn('[Library] Missing playable URL for track', {
                          trackId: track.id,
                          params: track.params,
                        });
                        return;
                      }
                      const isThisTrack = playingTrack?.id === track.id;
                      if (isThisTrack) {
                        handlePlayerToggle();
                      } else {
                        handlePlayTrack(track);
                      }
                    }}
                  >
                    <Thumbnail $imageUrl={artworkUrl} />
                    <PlayOverlay $isLoading={loading}>
                      {loading ? (
                        <LoaderSpinner />
                      ) : playingTrack?.id === track.id && isPlaying ? (
                        <svg viewBox="0 0 24 24" style={{ width: '20px', height: '20px', fill: '#6777FF'}}>
                          <rect x="6" y="4" width="4" height="16" fill='#6777FF' />
                          <rect x="14" y="4" width="4" height="16" fill='#6777FF' />
                        </svg>
                      ) : (
                        <PlayIcon />
                      )}
                    </PlayOverlay>
                    {durationLabel && !loading && (
                      <DurationBadge>{durationLabel}</DurationBadge>
                    )}
                  </ThumbnailWrapper>
                  <TrackInfo onClick={() => {
                    if (loading) return;
                    setSelectedTrack(track);
                  }}>
                    <TrackTitle>
                      {displayTitle}
                    </TrackTitle>
                    <TrackSubtitle>{subtitleShort}</TrackSubtitle>
                    <TrackActions onClick={(e) => e.stopPropagation()}>
                      <ReactionButton
                        title="Like"
                        $active={reaction === 'like'}
                        disabled={loading}
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (loading) return;
                          const next = reaction === 'like' ? null : 'like'; 
                          try {
                            await setTrackReaction(track.id, next as any);
                            setRefreshKey(k => k + 1);
                            window.dispatchEvent(new CustomEvent('trackReactionChanged', { 
                              detail: { trackId: track.id, reaction: next } 
                            }));
                          } catch (error) {
                            console.error('Failed to set track reaction:', error);
                            showToast('Failed to update reaction', undefined, 'error');
                          }
                        }}
                      >
                        <LikeIcon />
                      </ReactionButton>
                      <ReactionButton
                        title="Dislike"
                        $active={reaction === 'dislike'}
                        disabled={loading}
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (loading) return;
                          const next = reaction === 'dislike' ? null : 'dislike'; 
                          try {
                            await setTrackReaction(track.id, next as any);
                            setRefreshKey(k => k + 1);
                            window.dispatchEvent(new CustomEvent('trackReactionChanged', { 
                              detail: { trackId: track.id, reaction: next } 
                            }));
                          } catch (error) {
                            console.error('Failed to set track reaction:', error);
                            showToast('Failed to update reaction', undefined, 'error');
                          }
                        }}
                      >
                        <DislikeIcon />
                      </ReactionButton>
                      <ActionButton 
                        title="Export"
                        disabled={loading}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (loading) return;
                          setShareTrack(track);
                          setShareTrack(track);
                        }}
                      >
                        <ExportIcon />
                      </ActionButton>
                    </TrackActions>
                  </TrackInfo>
                  <MoreWrapper onClick={(e) => e.stopPropagation()} data-track-menu>
                    {!loading && (
                      <MoreButton onClick={(e) => { e.stopPropagation(); setMenuOpenId(menuOpenId === track.id ? null : track.id); setSubmenuOpen(null); }}>⋯</MoreButton>
                    )}
                    {!loading && menuOpenId === track.id && (
                      <TrackMenu
                        trackId={track.id}
                        track={track}
                        onClose={() => { setMenuOpenId(null); setSubmenuOpen(null); }}
                        onAddToPlaylist={() => { setAddToPlaylistTrack(track); setMenuOpenId(null); setSubmenuOpen(null); }}
                        onMoveToWorkspace={() => { setMoveToWorkspaceTrack(track); setMenuOpenId(null); setSubmenuOpen(null); }}
                        onCreatePersona={() => { setCreatePersonaTrack(track); setMenuOpenId(null); setSubmenuOpen(null); }}
                        onAddToQueue={() => {}}
                        onMoveToTrash={() => { setRefreshKey(k => k + 1); setSelectedTracks(new Set()); }}
                        onReportBug={() => { 
                          setReportBugTrack(track); 
                        }}
                        onReportInappropriate={() => { 
                          showToast("Marked, thanks!", undefined, 'success');
                        }}
                        onShareTo={() => {
                          setShareTrack(track);
                        }}
                        selectedTracks={selectedTracks}
                        submenuOpen={submenuOpen}
                        onSubmenuOpen={setSubmenuOpen}
                      />
                    )}
                  </MoreWrapper>
                </TrackItem>
              );
            })}
          </TrackList>
        )}
      </ContentWrapper>
      {selectedTrack && (
        <TrackModal
          track={selectedTrack}
          onClose={() => setSelectedTrack(null)}
          hasPlayer={hasPlayer}
          onPlayTrack={handlePlayTrack}
          playingTrack={playingTrack}
          isPlaying={isPlaying}
          onTogglePlay={handlePlayerToggle}
          source="library"
        />
      )}
      {playingTrack && (
        <MusicPlayer
          ref={playerRef}
          track={playingTrack}
          onClose={() => {
            setPlayingTrack(null);
            setIsPlaying(false);
            setSourceTracksForPlayer([]);
          }}
          onTrackChange={(newTrack) => setPlayingTrack(newTrack)}
          onTogglePlay={setIsPlaying}
          sourceTracks={sourceTracksForPlayer}
        />
      )}
      <AddToPlaylistModal
        open={!!addToPlaylistTrack}
        track={addToPlaylistTrack}
        selectedTrackIds={selectedTracks.size > 0 ? selectedTracks : undefined}
        onClose={() => { setAddToPlaylistTrack(null); setSelectedTracks(new Set()); }}
        onAdded={async () => { 
          setRefreshKey(k => k + 1); 
          setSelectedTracks(new Set());
          // Refresh playlists to show updated track lists
          try {
            await fetchPlaylists();
            // If we're viewing a playlist, refresh it
            if (selectedPlaylist) {
              const updated = listPlaylists().find((p: Playlist) => p.id === selectedPlaylist.id);
              if (updated) {
                setSelectedPlaylist(updated);
              }
            }
          } catch (error) {
            console.error('Failed to refresh playlists:', error);
          }
        }}
      />
      <MoveToWorkspaceModal
        open={!!moveToWorkspaceTrack}
        track={moveToWorkspaceTrack}
        selectedTrackIds={selectedTracks.size > 0 ? selectedTracks : undefined}
        onClose={() => { setMoveToWorkspaceTrack(null); setSelectedTracks(new Set()); }}
        onMoved={() => { setRefreshKey(k => k + 1); setSelectedTracks(new Set()); }}
      />
      <ReportBugModal
        open={!!reportBugTrack}
        track={reportBugTrack}
        onClose={() => { setReportBugTrack(null); }}
      />
      <ShareModal
        open={!!shareTrack || !!sharePlaylist}
        track={shareTrack}
        playlist={sharePlaylist}
        onClose={() => { setShareTrack(null); setSharePlaylist(null); }}
      />
      <CreatePersonaModal
        open={!!createPersonaTrack}
        track={createPersonaTrack}
        onClose={() => setCreatePersonaTrack(null)}
        onSuccess={() => {
          setRefreshKey(k => k + 1);
          setCreatePersonaTrack(null);
        }}
        origin="library"
      />
      <EditPlaylistModal
        open={editPlaylistOpen}
        playlist={editingPlaylist}
        onClose={() => {
          setEditPlaylistOpen(false);
          setEditingPlaylist(null);
        }}
        onSaved={async () => {
          await fetchPlaylists();
          setRefreshKey(k => k + 1);
          setEditPlaylistOpen(false);
          setEditingPlaylist(null);
        }}
      />
      <EditPlaylistModal
        open={editSelectedPlaylistOpen}
        playlist={selectedPlaylist}
        onClose={() => {
          setEditSelectedPlaylistOpen(false);
        }}
        onSaved={async () => {
          await fetchPlaylists();
          setRefreshKey(k => k + 1);
          setEditSelectedPlaylistOpen(false);
          // Refresh selectedPlaylist to show updated name/image
          if (selectedPlaylist) {
            const updated = listPlaylists().find((p: Playlist) => p.id === selectedPlaylist.id);
            if (updated) {
              setSelectedPlaylist(updated);
            }
          }
        }}
      />
    </Container>
  );
};

function getGradientForPlaylist(playlistId: string): string {
  const hash = playlistId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const gradients = [
    'linear-gradient(135deg, #ff9a9e, #fecfef)',
    'linear-gradient(135deg, #a1c4fd, #c2e9fb)',
    'linear-gradient(135deg, #ffecd2, #fcb69f)',
    'linear-gradient(135deg, #ff9a56, #ffad56)',
    'linear-gradient(135deg, #667eea, #764ba2)',
    'linear-gradient(135deg, #f093fb, #f5576c)',
    'linear-gradient(135deg, #4facfe, #00f2fe)',
    'linear-gradient(135deg, #43e97b, #38f9d7)',
  ];
  return gradients[hash % gradients.length];
}

const Container = styled.div<{ $hasPlayer?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding: 20px;
  padding-bottom: ${p => p.$hasPlayer ? '20px' : '20px'};
  overflow: hidden;
  box-sizing: border-box;
  transition: padding-bottom 0.3s ease;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  position: sticky;
  top: 0;
  z-index: 10;
  background: ${props => props.theme.palette.background.primary || '#fff'};
  padding-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const Tabs = styled.div`
  display: flex;
  gap: 8px;
  border-bottom: 1px solid #e9ecef;
`;

const Tab = styled.button<{ $active?: boolean }>`
  padding: 8px 16px;
  border: none;
  background: transparent;
  color: ${p => p.$active ? '#6777FF' : '#666'};
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  border-bottom: 2px solid ${p => p.$active ? '#6777FF' : 'transparent'};
  transition: all 0.2s ease;
  
  &:hover {
    color: #6777FF;
  }
`;

const FixedControls = styled.div<{ $hasModal: boolean }>`
  position: sticky;
  top: 0;
  z-index: 10;
  background: ${props => props.theme.palette.background.primary || '#fff'};
  padding-bottom: 20px;
  width: 100%;
  transition: width 0.3s ease;
`;

const ContentWrapper = styled.div<{ $hasModal: boolean; $hasPlayer?: boolean }>`
  width: 100%;
  flex: 1;
  min-height: 0;
  transition: width 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: ${p => p.$hasPlayer ? '120px' : '0'};
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  width: 100%;
  min-width: 0;
`;

const SearchInput = styled.input`
  flex: 1 1 40%; 
  max-width: 80%;
  min-width: 20%;
  height: 4.2vh;
  border-radius: 10px;
  border: 1px solid #e9ecef;
  padding: 0 1vw;
  outline: none;
  box-sizing: border-box;
`;

const DropdownWrapper = styled.div`
  position: relative;
  display: flex;
`;

const PillWithCaret = styled.button<{ $active?: boolean }>`
  padding: 1vh 1vw;
  border-radius: 20px;
  border: 1px solid ${p => p.$active ? '#000' : '#e9ecef'};
  background: #fff;
  color: #333;
  cursor: pointer;
  white-space: nowrap;
  flex: 0 0 auto;
  min-width: 0;
  padding-right: calc(1vw + 16px);
  position: relative;
  &::after {
    content: '';
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 6px solid #333;
    opacity: 0.7;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(0,0,0,0.12);
  padding: 8px 0;
  min-width: 240px;
  z-index: 20;
`;

const DropdownItem = styled.div`
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  label { cursor: pointer; user-select: none; display: flex; align-items: center; justify-content: space-between; width: 100%; }
  input { margin-left: 12px; }
  &:hover { background: #f8f9ff; }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 20px;
  height: 100%;
  min-height: 500px;
`;

const EmptyText = styled.div`
  color: #6c757d;
  font-size: 16px;
  text-align: center;
`;

const TrackList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CheckboxWrapper = styled.div`
  width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Checkbox = styled.input<{ $visible?: boolean }>`
  width: 18px;
  height: 18px;
  cursor: pointer;
  opacity: ${p => p.$visible ? 1 : 0};
  transition: opacity 0.15s ease;
`;

const Thumbnail = styled.div<{ $imageUrl?: string }>`
  width: 7.5vh;
  height: 9vh;
  border-radius: 6px;
  background: ${({ $imageUrl }) =>
    $imageUrl
      ? `url(${$imageUrl}) center center / cover no-repeat`
      : 'linear-gradient(135deg, #e8eaff, #f4f4f4)'};
  transition: opacity 0.15s ease;
`;

const DurationBadge = styled.div`
  position: absolute;
  right: 4px;
  bottom: 4px;
  padding: 2px 6px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  font-size: 10px;
  font-weight: 600;
`;

const PlayOverlay = styled.div<{ $isLoading?: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: ${p => p.$isLoading ? 1 : 0};
  transition: opacity 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ThumbnailWrapper = styled.div<{ $isLoading?: boolean }>`
  position: relative;
  cursor: ${p => p.$isLoading ? 'default' : 'pointer'};
  flex-shrink: 0;
  &:hover {
    ${Thumbnail} {
      opacity: ${p => p.$isLoading ? 1 : 0.7};
    }
    ${PlayOverlay} {
      opacity: 1;
    }
  }
`;

const TrackItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  background: #fff;
  transition: background 0.15s ease, box-shadow 0.15s ease;
    margin: 3px 10px;

  &:hover {
    background: #fff;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    ${Checkbox} {
      opacity: 1;
    }
  }
  ${Checkbox}[checked] {
    opacity: 1 !important;
  }
`;

const TrackInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  cursor: pointer;
`;

const TrackTitle = styled.div`
  font-weight: 600;
  color: #333;
`;

const TrackSubtitle = styled.div`
  color: #6c757d;
  font-size: 12px;
`;

const TrackActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ReactionButton = styled.button<{ $active?: boolean }>`
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${p => p.$active ? '#6777FF' : '#9aa0a6'};
  opacity: 0.7;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  &:hover { color: #6777FF; }
  svg { width: 20px; height: 20px; }
  svg * { fill: currentColor; }
  ${TrackItem}:hover & { opacity: 1; }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const ActionButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: #6777FF;
  opacity: 0.7;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  &:hover { opacity: 1; }
  svg { width: 20px; height: 20px; }
  svg * { fill: currentColor; }
  ${TrackItem}:hover & { opacity: 1; }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const MoreWrapper = styled.div`
  margin-left: auto;
  position: relative;
`;

const MoreButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 14px;
  border: 1px solid #e9ecef;
  background: #fff;
  cursor: pointer;
`;

const LoaderSpinner = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid rgba(103, 119, 255, 0.2);
  border-top-color: #6777FF;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const PlaylistList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  padding: 20px 0;
`;

const CreatePlaylistItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-radius: 12px;
  border: 2px dashed #e9ecef;
  background: transparent;
  cursor: pointer;
  transition: all 0.15s ease;
  min-height: 200px;
  align-items: center;
  justify-content: center;
  
  &:hover {
    border-color: #6777FF;
    background: rgba(103, 119, 255, 0.05);
  }
`;

const CreatePlaylistIcon = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  
  svg {
    width: 100%;
    height: 100%;
  }
  
  ${CreatePlaylistItem}:hover & {
    color: #6777FF;
  }
`;

const PlaylistItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  transition: background 0.15s ease, box-shadow 0.15s ease;
  
  &:hover {
    background: #F4F4F4;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
`;

const PlaylistItemHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const PlaylistItemMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`;

const PlaylistThumbnail = styled.div`
  width: 100%;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
`;

const PlaylistThumbnailImage = styled.div<{ $gradient?: string }>`
  width: 100%;
  height: 100%;
  background: ${p => p.$gradient || 'linear-gradient(135deg, #667eea, #764ba2)'};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PlaylistThumbnailImageImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const LikeIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PlaylistInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PlaylistName = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 14px;
`;

const PlaylistCount = styled.div`
  color: #6c757d;
  font-size: 12px;
`;

const PlaylistViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 20px 0;
  position: relative;
`;

const PlaylistHeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  width: 100%;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    flex-direction: column;
    align-items: stretch;
    gap: 20px;
  }
`;

const BackButton = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 0px;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
  margin-top: 0;
  
  &:hover {
    background: #transparent;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const PlaylistHeader = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;
  flex: 1;
  margin-top: 0;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const PlaylistCover = styled.div`
  width: 200px;
  height: 200px;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    width: 160px;
    height: 160px;
    margin: 0 auto;
  }
`;

const PlaylistCoverImage = styled.div<{ $gradient?: string }>`
  width: 100%;
  height: 100%;
  background: ${p => p.$gradient || 'linear-gradient(135deg, #667eea, #764ba2)'};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PlaylistCoverImageImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PlaylistHeaderInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 200px;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    align-items: center;
    justify-content: flex-start;
    min-height: auto;
    gap: 16px;
  }
`;

const PlaylistTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    justify-content: center;
    flex-wrap: wrap;
    text-align: center;
  }
`;

const PlaylistViewTitle = styled.div`
  font-size: 48px;
  font-weight: 700;
  color: #000;
  font-family: serif;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    font-size: 26px;
  }
`;

const PlaylistActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    gap: 6px;
  }
`;

const EditPlaylistButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: transparent;
  }
  
  svg {
    width: 14px;
    height: 14px;
  }

  @media (max-width: ${DeviceSize.TabletMedium}) {
    width: 28px;
    height: 28px;

    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

const DeletePlaylistButton = styled.button<{ disabled?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  background: transparent;
  cursor: ${p => p.disabled ? 'wait' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  opacity: ${p => p.disabled ? 0.5 : 1};

  &:hover:not(:disabled) {
    background: rgba(255, 0, 0, 0.05);
    border-color: #ffcccc;
  }

  svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: ${DeviceSize.TabletMedium}) {
    width: 28px;
    height: 28px;

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const PlaylistHeaderActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;

  @media (max-width: ${DeviceSize.TabletMedium}) {
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    gap: 8px;
    width: 100%;
    margin-top: 8px;
  }
`;

const PlayButton = styled.button<{ disabled?: boolean }>`
  height: 40px;
  padding: 0px 10px;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  background: #fff;
  color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${p => p.disabled ? 0.5 : 1};
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #f4f4f4;
    border-color: #ccc;
  }
  
  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  @media (max-width: ${DeviceSize.TabletMedium}) {
    width: 100%;
  }
`;

const ShareButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f4f4f4;
    border-color: #7987FF;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: ${DeviceSize.TabletMedium}) {
    align-self: center;
  }
`;

const PlayButtonText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #000;
  white-space: nowrap;
`;

const EmptyPlaylistState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  padding: 60px 20px;
  text-align: center;
`;

const EmptyPlaylistText = styled.div`
  color: #000;
  font-size: 18px;
  max-width: 80%;
`;

const EmptyPlaylistActions = styled.div`
  display: flex;
  gap: 12px;
`;

const CreateSongButton = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  background: linear-gradient(90deg, #7987FF 0%, #FF7B7D 100%);
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 0.9;
  }
`;

const GoToLibraryButton = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  background: #fff;
  color: #000;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
  
  &:hover {
    background: #f4f4f4;
    border-color: #d0d7de;
  }
`;

const PlaylistTrackList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export default Library;
